import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '@/core/components/Calendar/Calendar';
import { SelectTime } from '@/features/global-filter/components/sections-content/PeriodContent/components/SelectTime/SelectTime';
import { getInclusiveRangeStart } from '@/core/utils/dateUtils';
import './DatePicker.scss';

export interface DatePickerProps {
  startDate?: Date;
  endDate?: Date;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  maxRangeDays?: number;
  activeControl: string | null;
  onActiveControlChange: (id: string | null) => void;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
  maxRangeDays,
  activeControl,
  onActiveControlChange,
}) => {
  const { t } = useTranslation();
  const startCalendarOpen = activeControl === 'start-calendar';
  const endCalendarOpen = activeControl === 'end-calendar';

  const now = useMemo(() => new Date(), []);
  const today = useMemo(() => new Date(now.getFullYear(), now.getMonth(), now.getDate()), [now]);

  // Mirrors the clamping done by applyMaxRangeLimits, so the calendars can't show days
  // that would be rejected/clamped once a date outside the allowed window is picked.
  const restrictedMinDate = useMemo(
    () => (maxRangeDays === undefined ? undefined : getInclusiveRangeStart(today, maxRangeDays)),
    [maxRangeDays, today],
  );
  const endMinDate = useMemo(() => {
    if (!restrictedMinDate) return startDate;
    if (!startDate) return restrictedMinDate;
    return new Date(Math.max(startDate.getTime(), restrictedMinDate.getTime()));
  }, [startDate, restrictedMinDate]);

  const isEndDateToday = !!(endDate?.getFullYear() === now.getFullYear() && 
    endDate?.getMonth() === now.getMonth() && 
    endDate?.getDate() === now.getDate());

  const isSameDay = !!(
    startDate?.getFullYear() === endDate?.getFullYear() &&
    startDate?.getMonth() === endDate?.getMonth() &&
    startDate?.getDate() === endDate?.getDate()
  );

  const startH = startDate ? String(startDate.getHours()).padStart(2, '0') : '00';
  const startM = startDate ? String(startDate.getMinutes()).padStart(2, '0') : '00';
  const endH = endDate ? String(endDate.getHours()).padStart(2, '0') : '23';
  const endM = endDate ? String(endDate.getMinutes()).padStart(2, '0') : '59';

  const ascHours = useMemo(() => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')), []);
  const ascMinutes = useMemo(() => Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')), []);
  
  const descHours = useMemo(() => {
    if (isEndDateToday) {
      return Array.from({ length: now.getHours() + 1 }, (_, i) => String(now.getHours() - i).padStart(2, '0'));
    }
    if (isSameDay && startDate) {
      const startHour = startDate.getHours();
      const availableHours = 24 - startHour;
      return Array.from({ length: availableHours }, (_, i) => String(23 - i).padStart(2, '0'));
    }
    return Array.from({ length: 24 }, (_, i) => String(23 - i).padStart(2, '0'));
  }, [isEndDateToday, isSameDay, startDate, now]);

  const descMinutes = useMemo(() => {
    if (isEndDateToday && endDate?.getHours() === now.getHours()) {
      return Array.from({ length: now.getMinutes() + 1 }, (_, i) => String(now.getMinutes() - i).padStart(2, '0'));
    }
    if (isSameDay && startDate && endDate?.getHours() === startDate.getHours()) {
      const startMinute = startDate.getMinutes();
      const availableMinutes = 60 - startMinute;
      return Array.from({ length: availableMinutes }, (_, i) => String(59 - i).padStart(2, '0'));
    }
    return Array.from({ length: 60 }, (_, i) => String(59 - i).padStart(2, '0'));
  }, [isEndDateToday, isSameDay, startDate, endDate, now]);

  const capEndTimeToNow = (timeUnit: 'hour' | 'minute', value: number, currentHour: number): number => {
    if (timeUnit === 'hour' && value > now.getHours()) {
      return now.getHours();
    }
    if (timeUnit === 'minute' && currentHour === now.getHours() && value > now.getMinutes()) {
      return now.getMinutes();
    }
    return value;
  };

  const capEndTimeToStart = (timeUnit: 'hour' | 'minute', value: number, currentHour: number): number => {
    if (!startDate) return value;
    
    if (timeUnit === 'hour' && value < startDate.getHours()) {
      return startDate.getHours();
    }
    if (timeUnit === 'minute' && currentHour === startDate.getHours() && value < startDate.getMinutes()) {
      return startDate.getMinutes();
    }
    return value;
  };

  const isDateToday = (date: Date): boolean => {
    return date.getFullYear() === now.getFullYear() && 
      date.getMonth() === now.getMonth() && 
      date.getDate() === now.getDate();
  };

  const handleTimeChange = (type: 'start' | 'end', timeUnit: 'hour' | 'minute', value: string) => {
    if (!startDate || !endDate || !onDateRangeChange) return;

    const dates = { start: new Date(startDate), end: new Date(endDate) };
    let parsedValue = Number.parseInt(value, 10);
    const timeMethod = timeUnit === 'hour' ? 'setHours' : 'setMinutes';

    if (type === 'end') {
      if (isDateToday(endDate)) {
        parsedValue = capEndTimeToNow(timeUnit, parsedValue, endDate.getHours());
      }
      if (isSameDay) {
        parsedValue = capEndTimeToStart(timeUnit, parsedValue, endDate.getHours());
      }
    }

    dates[type][timeMethod](parsedValue);

    // If changing start time and dates are the same day, ensure end time is not before start time
    if (type === 'start' && isSameDay) {
      const newStartHour = dates.start.getHours();
      const newStartMinute = dates.start.getMinutes();
      const currentEndHour = dates.end.getHours();
      const currentEndMinute = dates.end.getMinutes();

      if (currentEndHour < newStartHour) {
        dates.end.setHours(newStartHour, newStartMinute, 59, 999);
      } else if (currentEndHour === newStartHour && currentEndMinute < newStartMinute) {
        dates.end.setMinutes(newStartMinute, 59, 999);
      }
    }

    onDateRangeChange(dates.start, dates.end);
  };

  const applyMaxRangeLimits = (
    finalStart: Date,
    finalEnd: Date,
    type: 'start' | 'end',
    maxDays: number,
  ): void => {
    const maxMs = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
    const minMs = getInclusiveRangeStart(now, maxDays).getTime();
    const maxDiffMs = maxDays * 24 * 60 * 60 * 1000;

    finalStart.setTime(Math.min(Math.max(finalStart.getTime(), minMs), maxMs));
    finalEnd.setTime(Math.min(Math.max(finalEnd.getTime(), minMs), maxMs));

    if (finalEnd.getTime() - finalStart.getTime() > maxDiffMs) {
      if (type === 'start') finalEnd.setTime(finalStart.getTime() + maxDiffMs);
      else finalStart.setTime(finalEnd.getTime() - maxDiffMs);
    }
  };

  const adjustEndTimeForToday = (endHour: number, endMinute: number): { hour: number; minute: number } => {
    let adjustedHour = endHour;
    let adjustedMinute = endMinute;

    if (endHour > now.getHours()) {
      adjustedHour = now.getHours();
      adjustedMinute = now.getMinutes();
    } else if (endHour === now.getHours() && endMinute > now.getMinutes()) {
      adjustedMinute = now.getMinutes();
    }

    return { hour: adjustedHour, minute: adjustedMinute };
  };

  const adjustEndTimeForSameDay = (
    endHour: number,
    endMinute: number,
    startHour: number,
    startMinute: number,
  ): { hour: number; minute: number } => {
    let adjustedHour = endHour;
    let adjustedMinute = endMinute;

    if (endHour < startHour) {
      adjustedHour = startHour;
      adjustedMinute = startMinute;
    } else if (endHour === startHour && endMinute < startMinute) {
      adjustedMinute = startMinute;
    }

    return { hour: adjustedHour, minute: adjustedMinute };
  };

  const areSameDayDates = (date1: Date, date2: Date): boolean => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const handleCalendarChange = (type: 'start' | 'end', newDate: Date | undefined) => {
    if (!onDateRangeChange || !newDate) return;

    const isNewEndDateToday = type === 'end' && isDateToday(newDate);

    const finalStart = new Date(type === 'start' ? newDate : (startDate ?? newDate));
    const finalEnd = new Date(type === 'end' ? newDate : (endDate ?? newDate));

    const startHour = Number.parseInt(startH, 10);
    const startMinute = Number.parseInt(startM, 10);
    let endHour = Number.parseInt(endH, 10);
    let endMinute = Number.parseInt(endM, 10);

    if (isNewEndDateToday) {
      const adjusted = adjustEndTimeForToday(endHour, endMinute);
      endHour = adjusted.hour;
      endMinute = adjusted.minute;
    }

    if (areSameDayDates(finalStart, finalEnd)) {
      const adjusted = adjustEndTimeForSameDay(endHour, endMinute, startHour, startMinute);
      endHour = adjusted.hour;
      endMinute = adjusted.minute;
    }

    finalStart.setHours(startHour, startMinute, 0, 0);
    finalEnd.setHours(endHour, endMinute, 59, 999);

    if (maxRangeDays !== undefined) {
      applyMaxRangeLimits(finalStart, finalEnd, type, maxRangeDays);
    }

    onDateRangeChange(finalStart, finalEnd);

    if (type === 'start') {
      onActiveControlChange('end-calendar');
    }
  };

  return (
    <div className="period-datepicker-wrapper">
      <div className="period-datepicker-inputs">
        <div className="period-datepicker-input-group">
          <label className="period-datepicker-label">{t('globalFilter.startDate')}</label>
          <div className="period-datepicker-row">
            <Calendar
              selectedDate={startDate}
              onDateSelect={(date) => handleCalendarChange('start', date)}
              isOpen={startCalendarOpen}
              onOpenChange={(open) => onActiveControlChange(open ? 'start-calendar' : null)}
              minDate={restrictedMinDate}
            />
            <SelectTime
              value={startH}
              onChange={(val) => handleTimeChange('start', 'hour', val)}
              options={ascHours}
              isOpen={activeControl === 'start-hour'}
              onOpen={() => onActiveControlChange('start-hour')}
              onClose={() => onActiveControlChange(null)}
            />
            <SelectTime
              value={startM}
              onChange={(val) => handleTimeChange('start', 'minute', val)}
              options={ascMinutes}
              isOpen={activeControl === 'start-minute'}
              onOpen={() => onActiveControlChange('start-minute')}
              onClose={() => onActiveControlChange(null)}
            />
          </div>
        </div>

        <div className="period-datepicker-input-group">
          <label className="period-datepicker-label">{t('globalFilter.endDate')}</label>
          <div className="period-datepicker-row">
            <Calendar
              selectedDate={endDate}
              onDateSelect={(date) => handleCalendarChange('end', date)}
              isOpen={endCalendarOpen}
              onOpenChange={(open) => onActiveControlChange(open ? 'end-calendar' : null)}
              minDate={endMinDate}
            />
            <SelectTime
              value={endH}
              onChange={(val) => handleTimeChange('end', 'hour', val)}
              options={descHours}
              isOpen={activeControl === 'end-hour'}
              onOpen={() => onActiveControlChange('end-hour')}
              onClose={() => onActiveControlChange(null)}
            />
            <SelectTime
              value={endM}
              onChange={(val) => handleTimeChange('end', 'minute', val)}
              options={descMinutes}
              isOpen={activeControl === 'end-minute'}
              onOpen={() => onActiveControlChange('end-minute')}
              onClose={() => onActiveControlChange(null)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};